/**
 * Caret helpers for contenteditable blocks — where is the caret, and how do
 * we split a block's content at it? Content is plain text (plus entities),
 * so cloned ranges serialize cleanly.
 */

export const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '');

/** Focused contenteditable + caret offset, or null if nothing is being edited. */
export const snapshotEditableCaret = () => {
  const el = document.activeElement;
  if (!el?.classList?.contains('doc-editable')) return null;
  const id = el.closest('[data-block-id]')?.getAttribute('data-block-id');
  if (!id) return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return { id, offset: null };
  return { id, offset: offsetWithinEditable(el, sel.anchorNode, sel.anchorOffset) };
};

/** Character offset of (node, offset) within el's textContent space. */
export const offsetWithinEditable = (el, node, nodeOffset) => {
  if (!el || !node || !el.contains(node)) return null;
  const pre = document.createRange();
  pre.selectNodeContents(el);
  try {
    pre.setEnd(node, nodeOffset);
  } catch {
    return null;
  }
  return pre.toString().length;
};

export const findTextPoint = (el, offset) => {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let remaining = Math.max(0, offset);
  let node = walker.nextNode();
  let last = null;
  while (node) {
    last = node;
    if (remaining <= node.textContent.length) {
      return { node, offset: remaining };
    }
    remaining -= node.textContent.length;
    node = walker.nextNode();
  }
  if (last) return { node: last, offset: last.textContent.length };
  return { node: el, offset: 0 };
};

/** Character offsets of the current selection inside `el`, or null. */
export const getSelectionOffsets = (el) => {
  if (!el) return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  if (!el.contains(sel.anchorNode) || !el.contains(sel.focusNode)) return null;
  const start = offsetWithinEditable(el, sel.anchorNode, sel.anchorOffset);
  const end = offsetWithinEditable(el, sel.focusNode, sel.focusOffset);
  if (start == null || end == null) return null;
  return { start, end };
};

/** Place a selection (or collapsed caret) at character offsets inside `el`. */
export const setSelectionOffsets = (el, start, end = start) => {
  if (!el) return false;
  const a = findTextPoint(el, start);
  const b = findTextPoint(el, end);
  if (!a || !b) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  if (typeof sel.setBaseAndExtent === 'function') {
    sel.setBaseAndExtent(a.node, a.offset, b.node, b.offset);
    return true;
  }
  const range = document.createRange();
  if (start <= end) {
    range.setStart(a.node, a.offset);
    range.setEnd(b.node, b.offset);
  } else {
    range.setStart(b.node, b.offset);
    range.setEnd(a.node, a.offset);
  }
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};

/** Boundary before/after an atomic inline (variable pill, mention, …). */
const pointBesideAtomic = (el, atomic, clientX) => {
  if (!el || !atomic || !el.contains(atomic)) return null;
  const rect = atomic.getBoundingClientRect();
  const after = clientX >= rect.left + rect.width / 2;
  const parent = atomic.parentNode;
  if (!parent) return null;
  const index = Array.prototype.indexOf.call(parent.childNodes, atomic);
  if (index < 0) return null;
  return { node: parent, offset: after ? index + 1 : index };
};

/** Caret position under client coords, if it falls inside el. */
export const caretPointInEditable = (el, clientX, clientY) => {
  if (!el) return null;
  if (typeof document.caretRangeFromPoint === 'function') {
    const range = document.caretRangeFromPoint(clientX, clientY);
    if (range && el.contains(range.startContainer)) {
      return { node: range.startContainer, offset: range.startOffset };
    }
  }
  if (typeof document.caretPositionFromPoint === 'function') {
    const pos = document.caretPositionFromPoint(clientX, clientY);
    if (pos && el.contains(pos.offsetNode)) {
      return { node: pos.offsetNode, offset: pos.offset };
    }
  }
  // Over a contenteditable=false pill the APIs above often miss — snap to
  // the token edge so drag-select can keep extending through it.
  if (typeof document.elementFromPoint !== 'function') return null;
  const hit = document.elementFromPoint(clientX, clientY);
  const atomic = hit?.closest?.(
    '[data-doc-variable], [data-doc-math], [data-doc-mention], .doc-editable code'
  );
  if (atomic && el.contains(atomic)) {
    return pointBesideAtomic(el, atomic, clientX);
  }
  return null;
};

/**
 * Character offset under a pointer event inside a .doc-editable, or null.
 * Used to remember a text-drag anchor before block multi-select clears it.
 */
export const textOffsetAtPoint = (editable, clientX, clientY) => {
  if (!editable) return null;
  const point = caretPointInEditable(editable, clientX, clientY);
  if (!point) return null;
  return offsetWithinEditable(editable, point.node, point.offset);
};

/**
 * Drive an in-block text highlight from a saved character-offset anchor to
 * the caret under (clientX, clientY). Used after drag-across block mode has
 * killed the browser's native select-drag.
 */
export const extendTextSelectionFromAnchor = (editable, anchorOffset, clientX, clientY) => {
  if (!editable || anchorOffset == null) return false;
  const focus = caretPointInEditable(editable, clientX, clientY);
  if (!focus) return false;
  const anchor = findTextPoint(editable, anchorOffset);
  if (!anchor) return false;
  editable.focus({ preventScroll: true });
  const sel = window.getSelection();
  if (!sel) return false;
  if (typeof sel.setBaseAndExtent === 'function') {
    sel.setBaseAndExtent(anchor.node, anchor.offset, focus.node, focus.offset);
  } else {
    const range = document.createRange();
    const a = document.createRange();
    a.setStart(anchor.node, anchor.offset);
    a.collapse(true);
    const b = document.createRange();
    b.setStart(focus.node, focus.offset);
    b.collapse(true);
    if (a.compareBoundaryPoints(Range.START_TO_START, b) <= 0) {
      range.setStart(anchor.node, anchor.offset);
      range.setEnd(focus.node, focus.offset);
    } else {
      range.setStart(focus.node, focus.offset);
      range.setEnd(anchor.node, anchor.offset);
    }
    sel.removeAllRanges();
    sel.addRange(range);
  }
  return true;
};

// Rendered text of an HTML string (entities decoded) — for caret offsets
export const htmlToText = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return div.textContent || '';
};

const rangeToHtml = (range) => {
  const div = document.createElement('div');
  div.appendChild(range.cloneContents());
  return div.innerHTML;
};

// Current collapsed selection inside el, or null
const getCaretRange = (el) => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;
  return range;
};

/**
 * Split el's content at the caret → { before, after } as HTML strings.
 * Returns null if there's no usable caret (e.g. a text selection).
 */
export const getCaretSplit = (el) => {
  const caret = getCaretRange(el);
  if (!caret) return null;

  const beforeRange = document.createRange();
  beforeRange.selectNodeContents(el);
  beforeRange.setEnd(caret.startContainer, caret.startOffset);

  const afterRange = document.createRange();
  afterRange.selectNodeContents(el);
  afterRange.setStart(caret.startContainer, caret.startOffset);

  return { before: rangeToHtml(beforeRange), after: rangeToHtml(afterRange) };
};

/**
 * Split el's content around a non-collapsed selection →
 * { before, selected, after } as HTML strings. Returns null if there is no
 * usable range inside el.
 */
export const getSelectionSplit = (el) => {
  if (!el) return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) {
    return null;
  }

  const beforeRange = document.createRange();
  beforeRange.selectNodeContents(el);
  beforeRange.setEnd(range.startContainer, range.startOffset);

  const afterRange = document.createRange();
  afterRange.selectNodeContents(el);
  afterRange.setStart(range.endContainer, range.endOffset);

  return {
    before: rangeToHtml(beforeRange),
    selected: rangeToHtml(range),
    after: rangeToHtml(afterRange),
  };
};

/** Ignore ZWSP caret buffers + a lone Chrome `<br>` when judging caret edges.
 *  Real spaces/newlines still count — only invisible chrome is skipped. */
const isVisuallyEmptyHtml = (html) => {
  const raw = (html || '').replace(/\u200B/g, '');
  if (!raw) return true;
  if (/^(?:<br\s*\/?>)+$/i.test(raw.trim())) return true;
  return stripHtml(raw).length === 0;
};

export const isCaretAtStart = (el) => {
  const split = getCaretSplit(el);
  return split !== null && isVisuallyEmptyHtml(split.before);
};

/** Line start including caret parked at offset 0 inside a leading <code>. */
export const isAtLineStart = (el) => {
  if (!el) return false;
  if (isCaretAtStart(el)) return true;
  const offsets = getSelectionOffsets(el);
  if (offsets && offsets.start === 0 && offsets.end === 0) return true;

  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const { anchorNode, anchorOffset } = sel;
  if (!anchorNode || (anchorNode !== el && !el.contains(anchorNode))) return false;
  if (anchorNode === el && anchorOffset === 0) return true;
  if (anchorNode.nodeType === Node.ELEMENT_NODE && anchorOffset === 0) {
    return el.firstChild === anchorNode || anchorNode === el;
  }
  if (anchorNode.nodeType === Node.TEXT_NODE && anchorOffset === 0) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    return walker.nextNode() === anchorNode;
  }
  return false;
};

export const isCaretAtEnd = (el) => {
  const split = getCaretSplit(el);
  return split !== null && isVisuallyEmptyHtml(split.after);
};

// Line detection for ArrowUp/ArrowDown navigation across blocks. Uses the
// caret's client rect against the element's bounds, so it works for wrapped
// paragraphs (only the first/last VISUAL line hands navigation over).
const getCaretTop = (el) => {
  const caret = getCaretRange(el);
  if (!caret) return null;
  const probe = caret.cloneRange();
  probe.collapse(true);
  const rects = probe.getClientRects();
  if (rects.length > 0) return { top: rects[0].top, bottom: rects[0].bottom };
  // Empty element / empty line: fall back to the element itself
  const rect = el.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom };
};

export const isCaretOnFirstLine = (el) => {
  if (stripHtml(el.innerHTML).length === 0) return true;
  const caret = getCaretTop(el);
  if (!caret) return false;
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
  return caret.top - el.getBoundingClientRect().top < lineHeight * 0.9;
};

export const isCaretOnLastLine = (el) => {
  if (stripHtml(el.innerHTML).length === 0) return true;
  const caret = getCaretTop(el);
  if (!caret) return false;
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
  return el.getBoundingClientRect().bottom - caret.bottom < lineHeight * 0.9;
};
